import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Unsubscribe,
  updateDoc,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Interfaces
export interface Notification {
  id?: string;
  type: 'follow' | 'like' | 'comment' | 'post';
  fromUserId: string;
  fromUserName: string;
  fromUserPhoto: string;
  toUserId: string;
  postId?: string;
  commentId?: string;
  read: boolean;
  createdAt?: any;
}

/**
 * Crea una nueva notificación
 * @param notification Datos de la notificación
 * @returns Promise<string> ID de la notificación creada
 */
export const createNotification = async (notification: Omit<Notification, 'id' | 'createdAt'>): Promise<string> => {
  try {
    const notificationRef = doc(collection(db, 'notifications'));
    await setDoc(notificationRef, {
      ...notification,
      createdAt: serverTimestamp()
    });

    console.log(`Notificación creada: ${notification.type} de ${notification.fromUserId} a ${notification.toUserId}`);
    return notificationRef.id;
  } catch (error) {
    console.error('Error al crear notificación:', error);
    throw new Error('No se pudo crear la notificación.');
  }
};

/**
 * Obtiene las notificaciones de un usuario
 * @param userId ID del usuario
 * @param limitCount Límite de notificaciones a obtener
 * @returns Promise<Notification[]>
 */
export const getUserNotifications = async (userId: string, limitCount: number = 20): Promise<Notification[]> => {
  try {
    const notificationsRef = collection(db, 'notifications');
    const notificationsQuery = query(
      notificationsRef,
      where('toUserId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const notificationsSnapshot = await getDocs(notificationsQuery);
    const notifications: Notification[] = [];

    notificationsSnapshot.forEach(doc => {
      notifications.push({
        id: doc.id,
        ...doc.data() as Omit<Notification, 'id'>
      });
    });

    return notifications;
  } catch (error) {
    console.error('Error al obtener notificaciones:', error);
    throw new Error('No se pudieron cargar las notificaciones. Inténtalo de nuevo.');
  }
};

/**
 * Marca una notificación como leída
 * @param userId ID del usuario
 * @param notificationId ID de la notificación
 * @returns Promise<void>
 */
export const markNotificationAsRead = async (userId: string, notificationId: string): Promise<void> => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);

    // Verificar que la notificación pertenezca al usuario
    const notificationDoc = await getDoc(notificationRef);
    if (!notificationDoc.exists()) {
      throw new Error('Notificación no encontrada');
    }

    const notificationData = notificationDoc.data() as Notification;
    if (notificationData.toUserId !== userId) {
      throw new Error('No tienes permiso para marcar esta notificación como leída');
    }

    await updateDoc(notificationRef, { read: true });
  } catch (error) {
    console.error('Error al marcar notificación como leída:', error);
    throw new Error('No se pudo marcar la notificación como leída.');
  }
};

/**
 * Marca todas las notificaciones de un usuario como leídas
 * @param userId ID del usuario
 * @returns Promise<void>
 */
export const markAllNotificationsAsRead = async (userId: string): Promise<void> => {
  try {
    const notificationsRef = collection(db, 'notifications');
    const notificationsQuery = query(
      notificationsRef,
      where('toUserId', '==', userId),
      where('read', '==', false)
    );

    const notificationsSnapshot = await getDocs(notificationsQuery);

    const batch = writeBatch(db);
    notificationsSnapshot.forEach(doc => {
      batch.update(doc.ref, { read: true });
    });

    await batch.commit();
  } catch (error) {
    console.error('Error al marcar todas las notificaciones como leídas:', error);
    throw new Error('No se pudieron marcar las notificaciones como leídas.');
  }
};

/**
 * Elimina una notificación
 * @param notificationId ID de la notificación
 * @returns Promise<void>
 */
export const deleteNotification = async (notificationId: string): Promise<void> => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await deleteDoc(notificationRef);
  } catch (error) {
    console.error('Error al eliminar notificación:', error);
    throw new Error('No se pudo eliminar la notificación.');
  }
};

/**
 * Configura un listener para las notificaciones de un usuario
 * @param userId ID del usuario
 * @param callback Función a ejecutar cuando cambien las notificaciones
 * @returns Función para cancelar el listener
 */
export const listenToNotifications = (
  userId: string,
  callback: (notifications: Notification[]) => void
): Unsubscribe => {
  const notificationsRef = collection(db, 'notifications');
  const notificationsQuery = query(
    notificationsRef,
    where('toUserId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(20)
  );

  return onSnapshot(notificationsQuery, (snapshot) => {
    const notifications: Notification[] = [];

    snapshot.forEach(doc => {
      notifications.push({
        id: doc.id,
        ...doc.data() as Omit<Notification, 'id'>
      });
    });

    callback(notifications);
  }, (error) => {
    console.error('Error en el listener de notificaciones:', error);
  });
};

/**
 * Obtiene el número de notificaciones no leídas de un usuario
 * @param userId ID del usuario
 * @returns Promise<number>
 */
export const getUnreadNotificationsCount = async (userId: string): Promise<number> => {
  try {
    const notificationsRef = collection(db, 'notifications');
    const notificationsQuery = query(
      notificationsRef,
      where('toUserId', '==', userId),
      where('read', '==', false)
    );

    const notificationsSnapshot = await getDocs(notificationsQuery);
    return notificationsSnapshot.size;
  } catch (error) {
    console.error('Error al obtener número de notificaciones no leídas:', error);
    return 0;
  }
};

/**
 * Configura un listener para el número de notificaciones no leídas de un usuario
 * @param userId ID del usuario
 * @param callback Función a ejecutar cuando cambie el número
 * @returns Función para cancelar el listener
 */
export const listenToUnreadNotificationsCount = (
  userId: string,
  callback: (count: number) => void
): Unsubscribe => {
  const notificationsRef = collection(db, 'notifications');
  const notificationsQuery = query(
    notificationsRef,
    where('toUserId', '==', userId),
    where('read', '==', false)
  );

  return onSnapshot(notificationsQuery, (snapshot) => {
    callback(snapshot.size);
  }, (error) => {
    console.error('Error en el listener de notificaciones no leídas:', error);
    callback(0);
  });
};
